import {
  checkIsHumidifierDoorOpened,
  checkIsHumidifierInstalled,
  checkWaterLevel,
  checkIdleHumidityEnabled,
  checkIdleHumidityTarget,
  checkIsHumidifierConnected,
  enableWaterPump,
} from 'api/humidifierApi';
import {
  FastRobotErrorcode,
  Feedback,
  HumidifierDoorStatus,
  ModalType,
  SignalREvent,
  SignalRHub,
} from 'constants/dataEnum';
import {hideLoading, showLoading, showSnackbar} from 'stores/actions/appStateAction';
import {
  setHumidifierDoorStatus,
  setHumidifierRuntime,
  setIdleHumidity,
  setIsHumidifierConnected,
  setIsHumidifierInstalled,
  setIsIdleHumidityEnabled,
  setIsWaterLevelLow,
} from 'stores/actions/humidifierAction';
import {selectIdleHumidity} from 'stores/selectors/humidifierSelectors';
import {batch} from 'react-redux';
import {showModalType} from 'stores/actions/typedModalAction';
import {invokeAPI, resolveIntl} from './commonBusiness';
import {showLowWaterLevelModal, showPressureDropModal} from './commonModalBusiness';
import {abortProtocol, pauseProtocol, resumeProtocol} from './instrumentBusiness';

const intl = resolveIntl();

export const updateIdleHumidityStatus = (idleHumidityValue, enabled) => async (dispatch) => {
  await dispatch(invokeAPI(SignalRHub.INSTRUMENT, SignalREvent.SET_IDLE_HUMIDITY, enabled, Number(idleHumidityValue)));

  if (enabled) dispatch(setIdleHumidity(idleHumidityValue));

  dispatch(setIsIdleHumidityEnabled(enabled));
  dispatch(
    showSnackbar(
      Feedback.SUCCESS,
      intl.formatMessage({
        id: 'idleHumidity.notification.set',
      }),
    ),
  );
};

const handleRuntimeUpdate = (data) => (dispatch) => {
  dispatch(setHumidifierRuntime(data));
};

const handleHumidifierDoorOpen = () => (dispatch) => {
  dispatch(setHumidifierDoorStatus(HumidifierDoorStatus.OPEN));
};

const handleHumidifierDoorClose = () => (dispatch) => {
  dispatch(setHumidifierDoorStatus(HumidifierDoorStatus.CLOSE));
};

const handleWaterLevelChange = (data) => (dispatch) => {
  const {isWaterLow} = data;

  dispatch(setIsWaterLevelLow(isWaterLow));
};

export const handleHumidityDropped =
  ({isWaterLevelLow, isProtocolRunning}) =>
  (dispatch, getState) => {
    const state = getState();
    const idleHumidity = selectIdleHumidity(state);

    let idTextModal;
    if (isProtocolRunning) {
      dispatch(pauseProtocol(false));

      idTextModal = isWaterLevelLow
        ? 'modal.humidifier.lowWaterLevel.runningProtocol'
        : 'modal.humidifier.humidityBelowSetPoint.runningProtocol';

      dispatch(
        showPressureDropModal(
          idTextModal,
          isProtocolRunning,
          () => {
            dispatch(resumeProtocol());
          },
          async () => {
            await dispatch(invokeAPI(SignalRHub.INSTRUMENT, SignalREvent.IGNORE_HUMIDITY_DROP, true));
            dispatch(resumeProtocol());
          },
          () => {
            dispatch(abortProtocol(false));
          },
        ),
      );
      return;
    }

    idTextModal = isWaterLevelLow ? 'modal.humidifier.lowWaterLevel' : 'modal.humidifier.humidityBelowSetPoint';

    dispatch(
      showPressureDropModal(
        idTextModal,
        isProtocolRunning,
        () => {
          dispatch(updateIdleHumidityStatus(idleHumidity, true));
        },
        () => {},
        () => {
          dispatch(updateIdleHumidityStatus(idleHumidity, false));
        },
      ),
    );
  };

const handleHumidifierConnectionChange = (data) => (dispatch) => {
  const {isConnected} = data;

  batch(() => {
    dispatch(setIsHumidifierConnected(isConnected));

    if (isConnected) {
      dispatch(fetchHumidifierLatestData());
    } else {
      dispatch(showModalType(ModalType.HUMIDIFIER_DISCONNECTED));
    }
  });
};

export const signalREvents = [
  {event: SignalREvent.IDLE_HUMIDITY_TEMPERATURE_UPDATE, handler: handleRuntimeUpdate},
  {event: SignalREvent.HUMIDIFIER_DOOR_OPEN, handler: handleHumidifierDoorOpen},
  {event: SignalREvent.HUMIDIFIER_DOOR_CLOSED, handler: handleHumidifierDoorClose},
  {event: SignalREvent.WATER_LEVEL_CHANGE, handler: handleWaterLevelChange},
  {event: SignalREvent.HUMIDITY_DROPPED, handler: handleHumidityDropped},
  {event: SignalREvent.HUMIDIFIER_CONNECTION_CHANGE, handler: handleHumidifierConnectionChange},
];

export const subscribeEvents = (hub) => (dispatch) => {
  signalREvents.forEach(({event, handler}) => {
    hub.on(event, (...args) => dispatch(handler(...args)));
  });
};

export const unsubscribeEvents = (hub) => () => {
  signalREvents.forEach(({event}) => {
    hub.off(event);
  });
};

export const checkAndUpdateIdleHumidityStatus = () => async (dispatch) => {
  try {
    const isEnabled = await checkIdleHumidityEnabled();
    dispatch(setIsIdleHumidityEnabled(isEnabled));

    if (isEnabled) {
      const idleHumidity = await checkIdleHumidityTarget();
      dispatch(setIdleHumidity(idleHumidity.toString()));
    }
  } catch (err) {
    console.error('Error in checkAndUpdateIdleHumidityStatus:', err);
  }
};

const fetchHumidifierLatestData = () => async (dispatch) => {
  try {
    const [isHumidifierDoorOpen, isWaterLevelLow] = await Promise.all([
      checkIsHumidifierDoorOpened(),
      checkWaterLevel(),
    ]);

    dispatch(checkAndUpdateIdleHumidityStatus());
    dispatch(setHumidifierDoorStatus(isHumidifierDoorOpen ? HumidifierDoorStatus.OPEN : HumidifierDoorStatus.CLOSE));
    dispatch(setIsWaterLevelLow(isWaterLevelLow));
  } catch (error) {
    console.error('Error in fetchHumidifierLatestData:', error);
  }
};

export const initializeHumidifierState = () => async (dispatch) => {
  try {
    const isHumidifierInstalled = await checkIsHumidifierInstalled();

    dispatch(setIsHumidifierInstalled(isHumidifierInstalled));

    if (!isHumidifierInstalled) return;

    const isHumidifierConnected = await checkIsHumidifierConnected();

    dispatch(setIsHumidifierConnected(isHumidifierConnected));

    if (isHumidifierConnected) {
      dispatch(fetchHumidifierLatestData());
    } else {
      dispatch(showModalType(ModalType.HUMIDIFIER_DISCONNECTED));
    }
  } catch (error) {
    console.error('Error in initializingHumidifierState:', error);
  }
};

export const checkAndPumpWater = () => async (dispatch) => {
  dispatch(showLoading(intl.formatMessage({id: 'humidifier.pumpingWater.loading'})));
  const resp = await enableWaterPump();
  dispatch(hideLoading());

  if (resp === FastRobotErrorcode.NoError) return true;

  return new Promise((resolve) => {
    dispatch(
      showLowWaterLevelModal(
        async () => {
          const levelOk = await dispatch(checkAndPumpWater());
          resolve(levelOk);
        },
        () => {
          dispatch(
            showSnackbar(
              Feedback.ERROR,
              intl.formatMessage({
                id: 'modal.humidifier.lowWaterLevel.warning.cancelMessage',
              }),
            ),
          );
          resolve(false);
        },
      ),
    );
  });
};
